import type { ReactNode } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { type CoreEnumResponse } from '@/api/core/core-enums/useCreateCoreEnum';
import { CoreEnumDTO } from '@/api/core/core-enums/useCoreEnums';
import { CoreEnumModalForm } from './CoreEnumModalForm';
import { useCoreEnumModal } from './useCoreEnumModal';

interface CoreEnumModalProps {
  open: boolean;
  onClose: () => void;
  enumToEdit?: CoreEnumDTO | null;
  /**
   * Called after a successful create/update with the saved enum (includes
   * options when the API returns them). Prefer this over relying on list
   * refetch timing when the caller needs to select the new id immediately.
   */
  onSaved?: (saved: CoreEnumResponse) => void;
  /** Override the default create-mode dialog description. */
  createDescription?: ReactNode;
  /** Seed option name placeholders for domain-specific create flows. */
  optionNamePlaceholder?: string;
}

/**
 * Shared create/edit dialog for Core Enums + their options. Used from org
 * settings and from inline pickers (e.g. task type outcome vocabulary).
 */
export function CoreEnumModal({
  open,
  onClose,
  enumToEdit,
  onSaved,
  createDescription,
  optionNamePlaceholder,
}: CoreEnumModalProps) {
  const modal = useCoreEnumModal({ open, onClose, enumToEdit, onSaved });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <CoreEnumModalForm
          modal={modal}
          enumToEdit={enumToEdit}
          createDescription={createDescription}
          optionNamePlaceholder={optionNamePlaceholder}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
